import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, XCircle, Dumbbell, Lightbulb } from "lucide-react";

function buildBlank(sentence, word) {
  const regex = new RegExp(`\\b${word}\\b`, "i");
  return sentence.replace(regex, "_____");
}

function buildQuiz(words, count = 10) {
  const withContext = words.filter((w) => w.contexts?.length > 0);
  const pool = [];
  words.forEach((w) => {
    pool.push({ type: "translate_to_native", word: w });
    pool.push({ type: "translate_to_target", word: w });
  });
  withContext.forEach((w) => {
    pool.push({ type: "fill_blank", word: w, sentence: w.contexts[w.contexts.length - 1].sentence });
  });
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, count);
}

export default function QuizTab() {
  const [quiz, setQuiz] = useState(null);
  const [index, setIndex] = useState(0);
  const [answer, setAnswer] = useState("");
  const [checked, setChecked] = useState(null);
  const [score, setScore] = useState(0);
  const [results, setResults] = useState([]);
  const [hintCount, setHintCount] = useState(0);

  const { data: words = [], isLoading } = useQuery({
    queryKey: ["words", "all", ""],
    queryFn: async () => (await api.get("/words", { params: { filter: "all" } })).data,
  });

  const startQuiz = () => {
    setQuiz(buildQuiz(words, 10));
    setIndex(0);
    setScore(0);
    setChecked(null);
    setAnswer("");
    setResults([]);
    setHintCount(0);
  };

  const item = quiz?.[index];

  const questionLabel = (q) => {
    if (q.type === "translate_to_native") return `Перевод слова "${q.word.word}"`;
    if (q.type === "translate_to_target") return `Слово по переводу "${q.word.translation}"`;
    return `Пропуск: "${buildBlank(q.sentence, q.word.word)}"`;
  };

  const correctAnswerFor = (q) => (q.type === "translate_to_native" ? q.word.translation : q.word.word);

  const showHint = () => setHintCount(1);
  const addHintLetter = () => {
    const correctAnswer = correctAnswerFor(item);
    setHintCount((c) => Math.min(c + 1, correctAnswer.length - 1));
  };
  const hint = hintCount > 0 ? (() => {
    const correctAnswer = correctAnswerFor(item);
    return correctAnswer.slice(0, hintCount) + "_".repeat(Math.max(0, correctAnswer.length - hintCount));
  })() : null;

  const checkAnswer = () => {
    if (!answer.trim()) return;
    const correctAnswer = item.type === "translate_to_native" ? item.word.translation : item.word.word;
    const isCorrect = answer.trim().toLowerCase() === correctAnswer.trim().toLowerCase();
    setChecked({ isCorrect, correctAnswer });
    setResults((r) => [...r, { question: questionLabel(item), userAnswer: answer, correctAnswer, isCorrect }]);
    if (isCorrect) setScore((s) => s + 1);
  };

  const next = () => {
    setIndex((i) => i + 1);
    setAnswer("");
    setChecked(null);
    setHintCount(0);
  };

  if (isLoading) {
    return <div className="text-center py-16 text-muted-foreground">Загрузка...</div>;
  }

  if (words.length === 0) {
    return (
      <div className="text-center py-16 text-muted-foreground" data-testid="quiz-empty-state">
        Добавьте хотя бы одно слово в словарь, чтобы начать тренировку
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center" data-testid="quiz-start-state">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
          <Dumbbell className="w-7 h-7 text-primary" />
        </div>
        <p className="font-medium">Тренировка слов из вашего словаря</p>
        <p className="text-sm text-muted-foreground max-w-xs">Заполнение пропусков и перевод слов — без ограничений FSRS, в любое время</p>
        <Button onClick={startQuiz} data-testid="start-quiz-button">Начать тренировку</Button>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-4 text-center max-w-md mx-auto" data-testid="quiz-complete-state">
        <CheckCircle2 className="w-10 h-10 text-emerald-500" />
        <p className="font-heading text-xl font-bold">Результат: {score} / {quiz.length}</p>
        <div className="w-full space-y-2 text-left" data-testid="quiz-results-breakdown">
          {results.map((r, i) => (
            <div
              key={i}
              className={`flex items-start gap-2 rounded-lg p-3 text-sm ${r.isCorrect ? "bg-emerald-50 dark:bg-emerald-900/20" : "bg-red-50 dark:bg-red-900/20"}`}
              data-testid={`quiz-result-item-${i}`}
            >
              {r.isCorrect ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              ) : (
                <XCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
              )}
              <div className="min-w-0">
                <p className="text-foreground">{r.question}</p>
                {!r.isCorrect && (
                  <p className="text-muted-foreground">
                    Ваш ответ: <span className="line-through">{r.userAnswer || "—"}</span> · Правильно: <span className="font-medium text-foreground">{r.correctAnswer}</span>
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
        <Button onClick={startQuiz} data-testid="quiz-restart-button">Пройти ещё раз</Button>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto py-6 space-y-5" data-testid="quiz-active-state">
      <Progress value={(index / quiz.length) * 100} className="h-1.5" />
      <p className="text-sm text-muted-foreground text-center">{index + 1} / {quiz.length}</p>

      <div className="bg-card border border-border rounded-2xl p-6 space-y-4 text-center">
        {item.type === "translate_to_native" && (
          <>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Переведите слово</p>
            <p className="font-heading text-3xl font-extrabold" data-testid="quiz-prompt">{item.word.word}</p>
          </>
        )}
        {item.type === "translate_to_target" && (
          <>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Как это по-английски?</p>
            <p className="font-heading text-3xl font-extrabold" data-testid="quiz-prompt">{item.word.translation}</p>
          </>
        )}
        {item.type === "fill_blank" && (
          <>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Заполните пропуск</p>
            <p className="text-xl font-medium" data-testid="quiz-prompt">{buildBlank(item.sentence, item.word.word)}</p>
          </>
        )}

        {!checked ? (
          <div className="space-y-2">
            <form onSubmit={(e) => { e.preventDefault(); checkAnswer(); }} className="flex gap-2">
              <Input autoFocus value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Ваш ответ..." data-testid="quiz-answer-input" />
              <Button type="submit" disabled={!answer.trim()} data-testid="quiz-check-button">Проверить</Button>
            </form>
            {hint ? (
              <div className="flex items-center justify-center gap-2">
                <p className="text-sm text-muted-foreground tracking-widest" data-testid="quiz-hint-text">{hint}</p>
                {hintCount < correctAnswerFor(item).length - 1 && (
                  <button
                    type="button"
                    onClick={addHintLetter}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
                    data-testid="quiz-hint-more-button"
                  >
                    <Lightbulb className="w-3.5 h-3.5" /> +1 буква
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={showHint}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary mx-auto"
                data-testid="quiz-hint-button"
              >
                <Lightbulb className="w-3.5 h-3.5" /> Подсказка
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className={`flex items-center justify-center gap-2 font-medium ${checked.isCorrect ? "text-emerald-600" : "text-red-600"}`} data-testid="quiz-result">
              {checked.isCorrect ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
              {checked.isCorrect ? "Правильно!" : `Правильный ответ: ${checked.correctAnswer}`}
            </div>
            <Button className="w-full" onClick={next} data-testid="quiz-next-button">Далее</Button>
          </div>
        )}
      </div>
    </div>
  );
}
